import React, { FC, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import s from './ModalFeedbackForm.module.sass'
import { useAppDispatch, useAppSelector } from '@app/model/store'
import { useField } from '@shared/lib/fields/hooks/useField/useField'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { Modal } from '@shared/ui/Overlays/Modal'
import { closeModal } from '@widgets/Common/ModalFeedback/model/actions'
import { Text } from '@shared/ui/Typography/Text'
import { BuildInput } from '@shared/ui/Form/Build/BuildInput'
import { BuildTextarea } from '@shared/ui/Form/Build/BuildTextarea'
import { Button } from '@shared/ui/Actions/Button'
import { TypeFeedbackCategorySelect } from '@widgets/Common/ModalFeedback/model/types'
import { FEEDBACK_CATEGORIES } from '@widgets/Common/ModalFeedback/const/categories'
import { resetCategory } from '@widgets/Common/ModalFeedback/model/slice'
import { formattedDataFormSend, TypeOutputDataItem } from '@widgets/Common/ModalFeedback/helpers/helpers'

interface PropsForm {
  handleIsValidFields: (value: boolean) => void
}

interface ChildFormMethods {
  getFormData: () => TypeOutputDataItem
}

const ComponentTicketNotReceived = forwardRef<ChildFormMethods, PropsForm>(({ handleIsValidFields }, ref) => {
  const { type, fields, title } = FEEDBACK_CATEGORIES.ticketNotReceived
  const [fieldEmail, setFieldEmail] = useField(fields.email)
  const [fieldPhone, setFieldPhone] = useField(fields.phone)
  const [fieldComment, setFieldComment] = useField(fields.comment)

  useImperativeHandle(ref, () => ({
    getFormData: () =>
      formattedDataFormSend(type, title, [
        { type: 'phone', value: fieldPhone.value, title: fieldPhone.settings.label },
        { type: 'email', value: fieldEmail.value, title: fieldEmail.settings.label },
        { type: 'comment', value: fieldComment.value, title: fieldComment.settings.label },
      ]),
  }))

  const isValid = [fieldPhone, fieldEmail, fieldComment].every((field) => field.valid)
  useEffect(() => {
    handleIsValidFields && handleIsValidFields(isValid)
  }, [isValid])

  return (
    <>
      <div className={s.row}>
        <BuildInput field={fieldEmail} setField={setFieldEmail} />
        <BuildInput field={fieldPhone} setField={setFieldPhone} />
      </div>

      <BuildTextarea field={fieldComment} setField={setFieldComment} />
    </>
  )
})
const ComponentTicketIssue: FC<PropsForm> = forwardRef(({ handleIsValidFields }, ref) => {
  const { type, fields, title } = FEEDBACK_CATEGORIES.ticketIssue
  const [fieldEmail, setFieldEmail] = useField(fields.email)
  const [fieldPhone, setFieldPhone] = useField(fields.phone)
  const [fieldOrder, setFieldOrder] = useField(fields.order)
  const [fieldComment, setFieldComment] = useField(fields.comment)

  useImperativeHandle(ref, () => ({
    getFormData: () =>
      formattedDataFormSend(type, title, [
        { type: 'phone', value: fieldPhone.value, title: fieldPhone.settings.label },
        { type: 'email', value: fieldEmail.value, title: fieldEmail.settings.label },
        { type: 'order', value: fieldOrder.value, title: fieldOrder.settings.label },
        { type: 'comment', value: fieldComment.value, title: fieldComment.settings.label },
      ]),
  }))

  const isValid = [fieldPhone, fieldOrder, fieldEmail, fieldComment].every((field) => field.valid)
  useEffect(() => {
    handleIsValidFields && handleIsValidFields(isValid)
  }, [isValid])

  return (
    <>
      <div className={s.row}>
        <BuildInput field={fieldEmail} setField={setFieldEmail} />
        <BuildInput field={fieldPhone} setField={setFieldPhone} />
      </div>

      <BuildInput field={fieldOrder} setField={setFieldOrder} />

      <BuildTextarea field={fieldComment} setField={setFieldComment} />
    </>
  )
})
const ComponentPaymentIssue: FC<PropsForm> = forwardRef(({ handleIsValidFields }, ref) => {
  const { type, fields, title } = FEEDBACK_CATEGORIES.paymentIssue
  const [fieldEmail, setFieldEmail] = useField(fields.email)
  const [fieldPhone, setFieldPhone] = useField(fields.phone)
  const [fieldComment, setFieldComment] = useField(fields.comment)

  useImperativeHandle(ref, () => ({
    getFormData: () =>
      formattedDataFormSend(type, title, [
        { type: 'phone', value: fieldPhone.value, title: fieldPhone.settings.label },
        { type: 'email', value: fieldEmail.value, title: fieldEmail.settings.label },
        { type: 'comment', value: fieldComment.value, title: fieldComment.settings.label },
      ]),
  }))

  const isValid = [fieldPhone, fieldEmail, fieldComment].every((field) => field.valid)
  useEffect(() => {
    handleIsValidFields && handleIsValidFields(isValid)
  }, [isValid])

  return (
    <>
      <div className={s.row}>
        <BuildInput field={fieldEmail} setField={setFieldEmail} />
        <BuildInput field={fieldPhone} setField={setFieldPhone} />
      </div>

      <BuildTextarea field={fieldComment} setField={setFieldComment} />
    </>
  )
})
const ComponentAiAssistantQuestion: FC<PropsForm> = forwardRef(({ handleIsValidFields }, ref) => {
  const { type, fields, title } = FEEDBACK_CATEGORIES.aiAssistantQuestion
  const [fieldEmail, setFieldEmail] = useField(fields.email)
  const [fieldComment, setFieldComment] = useField(fields.comment)

  useImperativeHandle(ref, () => ({
    getFormData: () =>
      formattedDataFormSend(type, title, [
        { type: 'email', value: fieldEmail.value, title: fieldEmail.settings.label },
        { type: 'comment', value: fieldComment.value, title: fieldComment.settings.label },
      ]),
  }))

  const isValid = [fieldEmail, fieldComment].every((field) => field.valid)
  useEffect(() => {
    handleIsValidFields && handleIsValidFields(isValid)
  }, [isValid])

  return (
    <>
      <BuildInput field={fieldEmail} setField={setFieldEmail} />
      <BuildTextarea field={fieldComment} setField={setFieldComment} />
    </>
  )
})
const ComponentCollaboration: FC<PropsForm> = forwardRef(({ handleIsValidFields }, ref) => {
  const { type, fields, title } = FEEDBACK_CATEGORIES.collaboration
  const [fieldEmail, setFieldEmail] = useField(fields.email)
  const [fieldPhone, setFieldPhone] = useField(fields.phone)
  const [fieldComment, setFieldComment] = useField(fields.comment)

  useImperativeHandle(ref, () => ({
    getFormData: () =>
      formattedDataFormSend(type, title, [
        { type: 'phone', value: fieldPhone.value, title: fieldPhone.settings.label },
        { type: 'email', value: fieldEmail.value, title: fieldEmail.settings.label },
        { type: 'comment', value: fieldComment.value, title: fieldComment.settings.label },
      ]),
  }))

  const isValid = [fieldPhone, fieldEmail, fieldComment].every((field) => field.valid)
  useEffect(() => {
    handleIsValidFields && handleIsValidFields(isValid)
  }, [isValid])

  return (
    <>
      <div className={s.row}>
        <BuildInput field={fieldEmail} setField={setFieldEmail} />
        <BuildInput field={fieldPhone} setField={setFieldPhone} />
      </div>

      <BuildTextarea field={fieldComment} setField={setFieldComment} />
    </>
  )
})
const ComponentGeneralQuestion: FC<PropsForm> = forwardRef(({ handleIsValidFields }, ref) => {
  const { type, fields, title } = FEEDBACK_CATEGORIES.generalQuestion
  const [fieldEmail, setFieldEmail] = useField(fields.email)
  const [fieldComment, setFieldComment] = useField(fields.comment)

  useImperativeHandle(ref, () => ({
    getFormData: () =>
      formattedDataFormSend(type, title, [
        { type: 'email', value: fieldEmail.value, title: fieldEmail.settings.label },
        { type: 'comment', value: fieldComment.value, title: fieldComment.settings.label },
      ]),
  }))

  const isValid = [fieldEmail, fieldComment].every((field) => field.valid)
  useEffect(() => {
    handleIsValidFields && handleIsValidFields(isValid)
  }, [isValid])

  return (
    <>
      <BuildInput field={fieldEmail} setField={setFieldEmail} />
      <BuildTextarea field={fieldComment} setField={setFieldComment} />
    </>
  )
})
const ComponentEdaCourseRefund: FC<PropsForm> = forwardRef(({ handleIsValidFields }, ref) => {
  const { type, fields, title } = FEEDBACK_CATEGORIES.edaCourseRefund
  const [fieldEmail, setFieldEmail] = useField(fields.email)
  const [fieldPhone, setFieldPhone] = useField(fields.phone)
  const [fieldOrder, setFieldOrder] = useField(fields.order)
  const [fieldComment, setFieldComment] = useField(fields.comment)

  useImperativeHandle(ref, () => ({
    getFormData: () =>
      formattedDataFormSend(type, title, [
        { type: 'phone', value: fieldPhone.value, title: fieldPhone.settings.label },
        { type: 'email', value: fieldEmail.value, title: fieldEmail.settings.label },
        { type: 'order', value: fieldOrder.value, title: fieldOrder.settings.label },
        { type: 'comment', value: fieldComment.value, title: fieldComment.settings.label },
      ]),
  }))

  const isValid = [fieldPhone, fieldOrder, fieldEmail, fieldComment].every((field) => field.valid)
  useEffect(() => {
    handleIsValidFields && handleIsValidFields(isValid)
  }, [isValid])

  return (
    <>
      <div className={s.row}>
        <BuildInput field={fieldEmail} setField={setFieldEmail} />
        <BuildInput field={fieldPhone} setField={setFieldPhone} />
      </div>
      <BuildInput field={fieldOrder} setField={setFieldOrder} />

      <BuildTextarea field={fieldComment} setField={setFieldComment} />
    </>
  )
})

const ComponentsForm = {
  ticketNotReceived: ComponentTicketNotReceived,
  ticketRefund: () => <></>,
  ticketIssue: ComponentTicketIssue,
  paymentIssue: ComponentPaymentIssue,
  aiAssistantQuestion: ComponentAiAssistantQuestion,
  collaboration: ComponentCollaboration,
  generalQuestion: ComponentGeneralQuestion,
  edaCourseRefund: ComponentEdaCourseRefund,
}

interface Props {
  category: TypeFeedbackCategorySelect
}

const ModalFeedbackForm: FC<Props> = ({ category }) => {
  const preselectedCategory = useAppSelector((store) => store.feedback.preselectedCategory)
  const childFormRef = useRef<ChildFormMethods>(null)
  const [isValidFields, setIsValidFields] = useState(false)
  const dispatch = useAppDispatch()
  const categoryData = FEEDBACK_CATEGORIES[category]

  const handleGoBack = () => {
    dispatch(resetCategory())
  }

  const handleSend = () => {
    if (childFormRef.current) {
      const formData = childFormRef.current.getFormData()
      console.log('Данные формы:', formData)
      // Отправка данных на сервер
    }
  }

  const handleIsValidFields = (value) => {
    setIsValidFields(value)
  }

  const ComponentForm = ComponentsForm[category]

  return (
    <ErrorBoundary>
      <Modal
        data-testid={'modal-feedback-form'}
        data-modal={categoryData.title}
        title={categoryData.title}
        size={'S'}
        handleClose={() => dispatch(closeModal())}
        className={s.modal}
        subtitle={
          preselectedCategory ? (
            <></>
          ) : (
            <Text sizes={'M'} onClick={handleGoBack} className={s['button-go-back']}>
              Изменить тему обращения
            </Text>
          )
        }>
        <div className={s.content}>
          <div className={s.form}>
            <ComponentForm ref={childFormRef} handleIsValidFields={handleIsValidFields} />
          </div>

          <Button
            sizes={'XL L'}
            mode={'black'}
            onClick={handleSend}
            disabled={!isValidFields}
            className={s.button}
            data-testid={'button-send'}>
            Отправить
          </Button>

          <Text sizes={'S'} className={s.meta}>
            Отправляя, вы даете{' '}
            <a href={'https://www.afisha.ru/article/usage/'} target={'_blank'}>
              согласие на обработку персональных данных
            </a>{' '}
            <br />и принимаете{' '}
            <a href={'https://www.afisha.ru/article/privacy-policy/'} target={'_blank'}>
              политику конфиденциальности
            </a>
          </Text>
        </div>
      </Modal>
    </ErrorBoundary>
  )
}

export default ModalFeedbackForm
